/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { Trans, useTranslation } from 'react-i18next';
import { API_OK, API_URL, API_EXCEPTION, DEFAULT_ERROR_TEXT } from '../../Constants';
import { apiErrorMessage, getLang, landOrResolve } from '../../Helper';
import { login, pending2FA } from '../../features/user';
import { isFeatureOn } from '../../portalSettings';
import { useRetryCountdown } from '../../hooks/useRetryCountdown';
import { useAutoFocus } from '../../hooks/useAutoFocus';
import { useWebOtp } from '../../hooks/useWebOtp';
import RetryNotice from '../widgets/RetryNotice';
import VoiceCallNotice from '../widgets/VoiceCallNotice';
import AccountChooser from '../widgets/AccountChooser';

/**
 * ההתחברות המהירה - כניסה בטלפון או במייל עם קוד חד-פעמי.
 *
 * 📄 התיעוד המלא: `ycserver/docs/quick-login.md`
 *
 * **רכיב אחד ולא ארבעה מסכים.** ארבעת השלבים הם אותה זרימה אחת, והם חולקים מצב שאסור
 * לו לעבור בין מסכים: הכרטיס, הערוץ, והיעד. שלב שהיה מסך משלו היה מקבל אותם ב-`state`
 * של הניווט - כלומר בערוץ שהדפדפן שולט בו - ומאבד אותם ברענון.
 *
 * **והוא מוטמע בשתי פריסות ההתחברות** (`ClassicLoginLayout`, `CellularLoginLayout`)
 * ואינו נתיב משלו: הבחירה בין מהיר לסיסמה היא ציר שני, אורתוגונלי לערכת העיצוב שכבר
 * בוחרת בין השתיים. נתיב לכל צירוף היה ארבעה מסכים במקום אחד.
 */

const PHASE_IDENTIFIER = 'identifier';
const PHASE_CODE = 'code';
const PHASE_ACCOUNTS = 'accounts';
/**
 * השלמת חשבון שלא הוכיח את הערוץ שהוקש.
 *
 * שתי דרכים, והראשונה מועדפת: קוד ל**ערוץ השני** של החשבון (מייל למי שנכנס בטלפון,
 * ולהפך), או מספר זיהוי. הראשונה מוכיחה שליטה בערוץ נוסף; השנייה היא ידיעה של נתון
 * שאינו סוד, והיא קיימת בשביל מי שאין לו ערוץ שני רשום.
 */
const PHASE_COMPLETION = 'completion';

const PHONE = 'PHONE';
const EMAIL = 'EMAIL';

/**
 * השגיאות שפירושן **הכרטיס מת** - ולא "נסה שוב על אותו מסך".
 *
 * הכרטיס פג, נשרף אחרי שנגמרו ניסיונות הזהות, או שהחשבון שהוכח כבר אינו נושא את
 * הערוץ. בכל אחד מהם אין מאחורי הטופס שעל המסך שום דבר, ולחיצה נוספת עליו תיענה
 * באותה שגיאה - כלומר הלקוח קורא הודעה, מנסה, וקורא אותה שוב.
 *
 * מה שנכון לעשות הוא להחזיר אותו לתחילת הזרימה **עם ההודעה**: משם יש לו מסלול
 * שעובד. ההגבלה אינה נחלשת מזה - הודעה חדשה נספרת בתקרת היעד, והסירובים שנצברו
 * כבר יושבים בסולם של הכתובת.
 */
const DEAD_TICKET_ERRORS = ['quick_login_ticket_invalid', 'quick_login_unavailable'];

export default function QuickLogin({ resellerData, onUsePassword, captcha, className = '' }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const allowPhone = isFeatureOn(resellerData, 'quickLoginByPhone');
    const allowEmail = isFeatureOn(resellerData, 'quickLoginByEmail');
    /** ההרשמה, לפי ההגדרה של הריסיילר - אותה שאלה בדיוק שמסך הסיסמה שואל. */
    const showRegister = isFeatureOn(resellerData, 'allowRegistration');

    const [state, setState] = useState({
        phase: PHASE_IDENTIFIER,
        /** ברירת המחדל היא הערוץ הפתוח הראשון - טלפון, כשהוא פתוח. */
        channel: allowPhone ? PHONE : EMAIL,
        identifier: '',
        /** `SMS` או `VOICE`. הבורר החי; מה שנשלח בפועל יושב ב-`sentMethod`. */
        phoneMethod: 'SMS',
        ticket: '',
        code: '',
        validId: '',
        validType: 'ID',
        accounts: [],
        /** מה נדרש כדי להשלים את החשבון שנבחר, כפי שהשרת מסר. */
        completion: null,
        /** הקוד של הערוץ השני, ומה שהשרת אמר עליו אחרי השליחה. */
        completionCode: '',
        completionSent: null,
        /** האם הלקוח עבר לחלופת מספר הזיהוי. */
        useIdentity: false,
        /**
         * היעד שהקוד יצא אליו, כפי שהשרת מסר אותו - ממוסך.
         *
         * **מיסוך של מה שהלקוח הקליד**, ולא של מה ששמור אצלנו: זו הנקודה שמאפשרת
         * להציג יעד בלי לגלות אם המזהה מוכר. שני הענפים מציגים את אותו דבר.
         */
        maskedDestination: '',
        /**
         * השיטה שבה הקוד יצא **בפועל** - ולא `phoneMethod`, שהוא הבורר.
         *
         * מסך הקוד נראה אחרת בשיחה (`VoiceCallNotice`), ובורר שזז אחרי השליחה אינו
         * משנה את מה שכבר קרה. אותה הבחנה בדיוק שנעשתה ב-`VerifyContact`.
         */
        sentMethod: null,
        busy: false,
        busyRef: null,
        error: null,
        /**
         * אישור על פעולה שהצליחה, ולא שגיאה - ולכן שדה נפרד.
         *
         * הוא קיים בשביל "שלחו לי שוב": מסך שאינו משתנה אחרי לחיצה נקרא כלחיצה שלא
         * נקלטה, והלקוח לוחץ שוב - וכל לחיצה שורפת אחת מהשליחות שבחלון.
         */
        notice: null,
    });

    /** הספירה עד שאפשר לבקש קוד נוסף. */
    const sendRetry = useRetryCountdown();
    /** ספירה שנייה, לניחוש הקוד - שתי תקרות שונות, ולכן שני מונים. ראה `VerifyContact`. */
    const submitRetry = useRetryCountdown();

    const [codeRef, focusCode] = useAutoFocus(state.phase === PHASE_CODE ? PHASE_CODE : null);

    /**
     * מילוי אוטומטי של הקוד שהגיע בסמס, והגשה - בשני מסכי הקוד של הזרימה.
     *
     * `sentMethod` ולא `phoneMethod`: הבורר יכול לזוז אחרי השליחה, ומה שהגיע בפועל
     * למכשיר הוא מה שיצא. בשיחה ובמייל אין מסרון לתפוס, וההאזנה אינה נפתחת כלל.
     *
     * ההגשה עוברת באותה פונקציה שהכפתור קורא לה, כלומר עם אותם שומרים - `busy`,
     * המונה, והכרטיס. מה שנחסך ללקוח הוא המעבר לאפליקציית ההודעות והלחיצה שאחריו.
     */
    useWebOtp(
        state.phase === PHASE_CODE && state.sentMethod === 'SMS',
        (code) => setState((s) => ({ ...s, code, error: null })),
        () => submitCode()
    );
    useWebOtp(
        state.phase === PHASE_COMPLETION && !state.useIdentity && state.completionSent?.method === 'SMS',
        (code) => setState((s) => ({ ...s, completionCode: code, error: null })),
        () => submitCompletionCode()
    );

    /**
     * `withCredentials` כדי שעוגיית המכשיר המזוהה תעבור.
     *
     * בלעדיה סימון "זכור את המכשיר" היה תיבה שנשמרת ואינה עושה דבר, ומכשיר מוכר לא
     * היה מדלג על הקוד השני. אותה תצורה בדיוק כמו ב-`login`.
     */
    const config = () => ({ headers: { lang: getLang() }, withCredentials: true });

    const fail = (reply) => {
        const code = reply?.data?.message;
        const message =
            reply?.data?.responseStatus === API_EXCEPTION && code
                ? apiErrorMessage(reply.data, t)
                : t(DEFAULT_ERROR_TEXT);
        const minutes = Number(reply?.data?.tryAgainInMinutes);
        return {
            message,
            minutes: minutes > 0 ? minutes : 0,
            dead: DEAD_TICKET_ERRORS.includes(code),
        };
    };

    /**
     * מנחית את התשובה של התחברות שהושלמה.
     *
     * **שלוש התשובות של השרת, ושלושתן מטופלות** - בדיוק כמו בשני מסכי ההתחברות
     * הקיימים: קוד שני, רישום שיטת אימות, ואימות אמצעי תקשורת. להנחית את הראשונים
     * בממשק פירושו לשלוח את הלקוח לדלת שתיטרק בפניו.
     */
    const landLogin = (data) => {
        if (data.requires2FA === true) {
            dispatch(pending2FA({ token: data.token }));
            navigate('/login-verify', {
                state: {
                    methodUuid: data.methodUuid || '',
                    trustedDevicesEnabled: data.trustedDevicesEnabled === true,
                },
            });
            return;
        }
        dispatch(login({ token: data.token }));
        if (data.requires2FAEnrollment === true) {
            navigate('/two-step-verification', { state: { afterLogin: true } });
            return;
        }
        if (data.requiresContactVerification === true) {
            navigate('/verify-contact', { state: { afterLogin: true } });
            return;
        }
        landOrResolve(data, navigate);
    };

    /* ------------------------------ שלב א: המזהה ------------------------------ */

    const submitIdentifier = async (e) => {
        e.preventDefault();
        if (state.busy || !state.identifier.trim()) return;

        const method = state.channel === EMAIL ? EMAIL : state.phoneMethod;
        const body = {
            channel: state.channel,
            identifier: state.identifier.trim(),
            method,
        };

        // הטוקן מצורף כאן, כדי שהפעולה שהוא נוצר עבורה תיקבע במקום אחד.
        const payload = captcha ? await captcha.attach(body) : body;
        if (!payload) return;

        setState((s) => ({ ...s, busy: true, error: null, notice: null }));
        try {
            const reply = await axios.post(API_URL + 'quickLoginStart', payload, config());
            if (reply?.data?.responseStatus !== API_OK) {
                const { message, minutes } = fail(reply);
                if (minutes) sendRetry.startFromMinutes(minutes);
                setState((s) => ({ ...s, busy: false, error: message }));
                return;
            }
            sendRetry.start(reply.data.resendInSeconds || 60);
            setState((s) => ({
                ...s,
                busy: false,
                phase: PHASE_CODE,
                ticket: reply.data.ticket,
                maskedDestination: reply.data.maskedDestination,
                sentMethod: reply.data.method,
                code: '',
            }));
            focusCode();
        } catch {
            setState((s) => ({ ...s, busy: false, error: t(DEFAULT_ERROR_TEXT) }));
        }
    };

    /**
     * שליחה חוזרת - **אותה קריאה, לאותו כרטיס**.
     *
     * הערוץ והיעד יושבים על הכרטיס בשרת ואינם נשלחים שוב, ולכן אין מסלול שני שיכול
     * לסטות מהראשון ולשלוח קוד למקום שאינו זה שכתוב על המסך. מה שמשתנה כאן הוא דבר
     * אחד: הודעת האישור שנשארת, שהיא ההגנה מפני לחיצות חוזרות.
     */
    const resend = async () => {
        if (state.busy || sendRetry.isWaiting) return;
        setState((s) => ({ ...s, busy: true, error: null, notice: null }));
        try {
            const reply = await axios.post(
                API_URL + 'quickLoginResend',
                { ticket: state.ticket },
                config()
            );
            if (reply?.data?.responseStatus !== API_OK) {
                const { message, minutes, dead } = fail(reply);
                if (dead) return restart(message);
                if (minutes) sendRetry.startFromMinutes(minutes);
                setState((s) => ({ ...s, busy: false, error: message }));
                return;
            }
            sendRetry.start(reply.data.resendInSeconds || 60);
            setState((s) => ({
                ...s,
                busy: false,
                code: '',
                notice: t('A new code was sent to the same destination'),
            }));
            focusCode();
        } catch {
            setState((s) => ({ ...s, busy: false, error: t(DEFAULT_ERROR_TEXT) }));
        }
    };

    /* ------------------------------ שלב ב: הקוד ------------------------------ */

    const submitCode = async (e) => {
        // אופציונלי, כי המילוי האוטומטי מגיש בלי אירוע. ראה `useWebOtp`.
        e?.preventDefault();
        if (state.busy || !state.code.trim() || submitRetry.isWaiting) return;

        setState((s) => ({ ...s, busy: true, error: null, notice: null }));
        try {
            const reply = await axios.post(
                API_URL + 'quickLoginVerify',
                {
                    ticket: state.ticket,
                    code: state.code.trim(),
                },
                config()
            );
            if (reply?.data?.responseStatus !== API_OK) {
                const { message, minutes, dead } = fail(reply);
                if (dead) return restart(message);
                if (minutes) submitRetry.startFromMinutes(minutes);
                setState((s) => ({ ...s, busy: false, error: message, code: '' }));
                return;
            }

            const data = reply.data;
            if (data.stage === 'ACCOUNT_CHOICE') {
                setState((s) => ({
                    ...s,
                    busy: false,
                    phase: PHASE_ACCOUNTS,
                    accounts: data.accounts || [],
                }));
                return;
            }
            if (data.stage === 'COMPLETION_REQUIRED') {
                enterCompletion(data);
                return;
            }
            landLogin(data);
        } catch {
            setState((s) => ({ ...s, busy: false, error: t(DEFAULT_ERROR_TEXT) }));
        }
    };

    /* ----------------------- שלב ג: השלמת חשבון שלא אומת ----------------------- */

    /** נכנס לשלב ההשלמה עם מה שהשרת אמר שנדרש. */
    const enterCompletion = (data) => {
        sendRetry.reset();
        setState((s) => ({
            ...s,
            busy: false,
            busyRef: null,
            phase: PHASE_COMPLETION,
            completion: {
                secondChannel: data.secondChannel || null,
                maskedDestination: data.maskedDestination || null,
                identityAllowed: data.identityAllowed === true,
            },
            // בלי ערוץ שני נותרה רק הת.ז., ואין טעם להציג בורר בין שתי דרכים.
            useIdentity: !data.secondChannel,
            completionCode: '',
            completionSent: null,
            validId: '',
            error: null,
            notice: null,
        }));
    };

    /**
     * שולח את הקוד לערוץ השני.
     *
     * **לא אוטומטי בבחירה**, ובמכוון: הלקוח בוחר קודם כיצד להשלים, ורק אז יוצאת
     * הודעה - אחרת היינו משלמים על ערוץ גם למי שיבחר להשלים במספר זיהוי.
     */
    const sendCompletionCode = async () => {
        if (state.busy || sendRetry.isWaiting) return;
        setState((s) => ({ ...s, busy: true, error: null, notice: null }));
        try {
            const reply = await axios.post(
                API_URL + 'quickLoginCompletionSend',
                { ticket: state.ticket },
                config()
            );
            if (reply?.data?.responseStatus !== API_OK) {
                const { message, minutes, dead } = fail(reply);
                if (dead) return restart(message);
                if (minutes) sendRetry.startFromMinutes(minutes);
                setState((s) => ({ ...s, busy: false, error: message }));
                return;
            }
            sendRetry.start(reply.data.resendInSeconds || 60);
            setState((s) => ({
                ...s,
                busy: false,
                completionCode: '',
                completionSent: {
                    channel: reply.data.channel,
                    maskedDestination: reply.data.maskedDestination,
                    method: reply.data.method,
                },
            }));
        } catch {
            setState((s) => ({ ...s, busy: false, error: t(DEFAULT_ERROR_TEXT) }));
        }
    };

    const submitCompletionCode = async (e) => {
        // אופציונלי, כי המילוי האוטומטי מגיש בלי אירוע. ראה `useWebOtp`.
        e?.preventDefault();
        // `submitRetry` נבדק כאן כמו במסך הקוד הראשון: לקוח שממשיך ללחוץ אחרי שנגמרו
        // הניחושים שורף את הכרטיס במקום להמתין.
        if (state.busy || !state.completionCode.trim() || submitRetry.isWaiting) return;
        setState((s) => ({ ...s, busy: true, error: null, notice: null }));
        try {
            const reply = await axios.post(
                API_URL + 'quickLoginCompletionVerify',
                {
                    ticket: state.ticket,
                    code: state.completionCode.trim(),
                },
                config()
            );
            if (reply?.data?.responseStatus !== API_OK) {
                const { message, minutes, dead } = fail(reply);
                if (dead) return restart(message);
                if (minutes) submitRetry.startFromMinutes(minutes);
                setState((s) => ({ ...s, busy: false, error: message, completionCode: '' }));
                return;
            }
            landLogin(reply.data);
        } catch {
            setState((s) => ({ ...s, busy: false, error: t(DEFAULT_ERROR_TEXT) }));
        }
    };

    const submitIdentity = async (e) => {
        e.preventDefault();
        if (state.busy || !String(state.validId).trim()) return;

        setState((s) => ({ ...s, busy: true, error: null }));
        try {
            const reply = await axios.post(
                API_URL + 'quickLoginIdentity',
                {
                    ticket: state.ticket,
                    validId: Number(state.validId),
                    validType: state.validType,
                },
                config()
            );
            if (reply?.data?.responseStatus !== API_OK) {
                const { message, dead } = fail(reply);
                /**
                 * **הניסיון האחרון נופל לכאן**, והשרת סוגר את הכרטיס ומחזיר
                 * `quick_login_ticket_invalid` - ולא "הפרטים אינם תואמים, נותרו 0".
                 * שתי התשובות תיארו את אותו מצב, ורק אחת מהן נכונה: הכרטיס כבר מת.
                 */
                if (dead) return restart(message);
                setState((s) => ({ ...s, busy: false, error: message }));
                return;
            }
            landLogin(reply.data);
        } catch {
            setState((s) => ({ ...s, busy: false, error: t(DEFAULT_ERROR_TEXT) }));
        }
    };

    /* --------------------------- שלב ד: בחירת חשבון --------------------------- */

    const chooseAccount = async (accountRef) => {
        if (state.busy) return;
        setState((s) => ({ ...s, busy: true, busyRef: accountRef, error: null }));
        try {
            const reply = await axios.post(
                API_URL + 'quickLoginChoose',
                {
                    ticket: state.ticket,
                    accountRef,
                },
                config()
            );
            if (reply?.data?.responseStatus !== API_OK) {
                const { message, dead } = fail(reply);
                if (dead) return restart(message);
                setState((s) => ({ ...s, busy: false, busyRef: null, error: message }));
                return;
            }
            if (reply.data.stage === 'COMPLETION_REQUIRED') {
                enterCompletion(reply.data);
                return;
            }
            landLogin(reply.data);
        } catch {
            setState((s) => ({ ...s, busy: false, busyRef: null, error: t(DEFAULT_ERROR_TEXT) }));
        }
    };

    /**
     * חזרה לתחילת הזרימה. הכרטיס נזנח בשרת ופג מעצמו.
     *
     * `error` נשאר על המסך כשהחזרה נכפתה - כרטיס שמת, ניסיונות שנגמרו - כי אחרת
     * הלקוח מוצא את עצמו בהתחלה בלי לדעת למה. בלחיצה על "להתחיל מחדש" הוא ריק,
     * כי שם הלקוח הוא זה שביקש.
     */
    const restart = (error = null) => {
        sendRetry.reset();
        submitRetry.reset();
        setState((s) => ({
            ...s,
            phase: PHASE_IDENTIFIER,
            ticket: '',
            code: '',
            validId: '',
            accounts: [],
            completion: null,
            completionCode: '',
            completionSent: null,
            useIdentity: false,
            sentMethod: null,
            busy: false,
            busyRef: null,
            error,
            notice: null,
        }));
    };

    /* --------------------------------- תצוגה --------------------------------- */

    /*
     * **מחלקות משלו, ולא של המסך שמארח אותו.**
     *
     * הגרסה הראשונה השתמשה במחלקות של הזרימה המלאה (`input blue-border`,
     * `button-teal`, `h2 teal`), והתוצאה במסך הסלולרי הייתה רכיב מושתל: שדות
     * מרובעים ולחצן זעיר על כרטיס שכל השאר בו מעוגל, וטקסט בגודל כותרת. גיליון אחד
     * לא יכול היה לתקן את זה בלי לדרוס את המסכים האחרים שמשתמשים באותן מחלקות.
     *
     * `ql-*` הן שמות של **תפקידים** במסך הזה, והן מעוצבות פעמיים: ב-`styles.css`
     * לזרימה המלאה, ותחת `.quick-login-sim` ב-`stylesSim.css` לעיצוב הסלולרי. אותו
     * רכיב, שני מראות, ואפס דריסות.
     */

    const channelLabel = state.channel === EMAIL ? t('Email address') : t('Phone number');

    return (
        <div className={`quick-login ${className}`}>
            {state.phase === PHASE_IDENTIFIER && (
                <form onSubmit={submitIdentifier}>
                    <p className='ql-title'>
                        {allowEmail && allowPhone
                            ? t('Enter your phone number or email and we will send you a code')
                            : allowEmail
                                ? t('Enter your email and we will send you a code')
                                : t('Enter your phone number and we will send you a code')}
                    </p>

                    {/* הבורר מוצג רק כששני הערוצים פתוחים: בורר עם אפשרות אחת הוא
                        רכיב שאינו בוחר דבר, והוא רק מאריך את המסך. */}
                    {allowPhone && allowEmail && (
                        <div className='ql-chips'>
                            <button
                                type='button'
                                className={state.channel === PHONE ? 'ql-chip ql-chip-on' : 'ql-chip'}
                                onClick={() => setState((s) => ({ ...s, channel: PHONE, identifier: '', error: null }))}
                            >
                                {t('Phone number')}
                            </button>
                            <button
                                type='button'
                                className={state.channel === EMAIL ? 'ql-chip ql-chip-on' : 'ql-chip'}
                                onClick={() => setState((s) => ({ ...s, channel: EMAIL, identifier: '', error: null }))}
                            >
                                {t('Email address')}
                            </button>
                        </div>
                    )}

                    <div className='ql-input'>
                        <input
                            type={state.channel === EMAIL ? 'email' : 'tel'}
                            id='identifier'
                            name='identifier'
                            dir='ltr'
                            autoComplete={state.channel === EMAIL ? 'email' : 'tel'}
                            placeholder={channelLabel}
                            value={state.identifier}
                            onChange={(e) => setState((s) => ({ ...s, identifier: e.target.value, error: null }))}
                        />
                    </div>

                    {/* בורר השיטה לטלפון בלבד. במייל אין מה לבחור. */}
                    {state.channel === PHONE && (
                        <div className='ql-options'>
                            <label>
                                <input
                                    type='radio'
                                    name='phoneMethod'
                                    checked={state.phoneMethod === 'SMS'}
                                    onChange={() => setState((s) => ({ ...s, phoneMethod: 'SMS' }))}
                                />
                                <span>{t('By SMS')}</span>
                            </label>
                            <label>
                                <input
                                    type='radio'
                                    name='phoneMethod'
                                    checked={state.phoneMethod === 'VOICE'}
                                    onChange={() => setState((s) => ({ ...s, phoneMethod: 'VOICE' }))}
                                />
                                <span>{t('By phone call')}</span>
                            </label>
                        </div>
                    )}

                    {captcha && captcha.node}

                    <button
                        type='submit'
                        className='ql-button'
                        disabled={state.busy}
                        aria-busy={state.busy ? 'true' : undefined}
                    >
                        {state.busy ? t('Sending...') : t('Send me a code')}
                        {state.busy && <span className='button-spinner' />}
                    </button>
                </form>
            )}

            {state.phase === PHASE_CODE && (
                <form onSubmit={submitCode}>
                    <p className='ql-title'>{t('Enter the code we sent you')}</p>
                    <p className='ql-destination' dir='ltr'>{state.maskedDestination}</p>

                    {/* ההסבר על קוד שמגיע בשיחה - ורק אחרי שהשיחה יצאה בפועל.
                        בלעדיו הלקוח ממתין למסרון שלא יגיע. ראה docs/voice-otp-notice.md */}
                    {state.sentMethod === 'VOICE' && <VoiceCallNotice />}

                    <div className='ql-input'>
                        <input
                            ref={codeRef}
                            type='tel'
                            inputMode='numeric'
                            autoComplete='one-time-code'
                            id='code'
                            name='code'
                            dir='ltr'
                            placeholder={t('Verification code')}
                            value={state.code}
                            onChange={(e) => setState((s) => ({ ...s, code: e.target.value, error: null }))}
                        />
                    </div>

                    <button
                        type='submit'
                        className='ql-button'
                        disabled={state.busy || submitRetry.isWaiting}
                        aria-busy={state.busy ? 'true' : undefined}
                    >
                        {state.busy ? t('Checking...') : t('Login')}
                    </button>

                    {/* תקרת הניחושים - זו כן שגיאה: הקוד נדחה, והלקוח ממתין בגללה. */}
                    <RetryNotice
                        retry={submitRetry}
                        i18nKey='Too many attempts. You can try again in {{time}}'
                    />

                    {/* "שלחו לי שוב" - חסום כשהתקרה נסגרה: לחיצה אז רק מחזירה סירוב,
                        והספירה שרצה מתחת ממילא אומרת כמה נותר. */}
                    <button
                        type='button'
                        className='ql-link'
                        disabled={sendRetry.isWaiting || state.busy}
                        onClick={resend}
                    >
                        <Trans i18nKey="Didn't get the code? <strong>Send it again</strong>" />
                    </button>
                    {/*
                        ספירת הצינון של השליחה - **"ניתן לשלוח קוד חדש בעוד", ולא
                        "יש לנסות שוב".**

                        היא רצה מיד אחרי שליחה שהצליחה, כלומר ברגע שבו שום דבר לא
                        נכשל: "יש לנסות שוב" אומר ללקוח שמה שעשה לא עבד, והוא ילחץ
                        שוב - וכל לחיצה כזו שורפת אחת מהשליחות שבחלון. `tone='info'`
                        מסיר גם את האדום, מאותו טעם.

                        זהו המפתח שכבר קיים ומשמש ב-`TwoStepVerification`: אותה
                        ספירה, אותה משמעות, ולכן אותו נוסח.
                    */}
                    <RetryNotice
                        retry={sendRetry}
                        i18nKey='A new code can be sent in {{time}}'
                        tone='info'
                    />
                </form>
            )}

            {state.phase === PHASE_COMPLETION && (
                <div>
                    <p className='ql-title'>{t('One more step, to confirm this account is yours')}</p>

                    {/*
                        **הערוץ השני קודם, והת.ז. מתחתיו.**

                        הראשון מוכיח שליטה בערוץ נוסף של החשבון; השני הוא ידיעה של
                        מספר שאינו סוד. הסדר על המסך הוא הסדר שבו אנחנו רוצים שהלקוח
                        יבחר, והחלופה מוצגת רק כשהיא באמת פתוחה.
                    */}
                    {!state.useIdentity && state.completion?.secondChannel && (
                        <form onSubmit={submitCompletionCode}>
                            <p className='ql-hint'>
                                {state.completion.secondChannel === 'EMAIL'
                                    ? t('We will send a code to the email address registered on this account')
                                    : t('We will send a code to the phone number registered on this account')}
                            </p>
                            <p className='ql-destination' dir='ltr'>
                                {state.completionSent?.maskedDestination || state.completion.maskedDestination}
                            </p>

                            {!state.completionSent ? (
                                <button
                                    type='button'
                                    className='ql-button'
                                    disabled={state.busy}
                                    aria-busy={state.busy ? 'true' : undefined}
                                    onClick={sendCompletionCode}
                                >
                                    {state.busy ? t('Sending...') : t('Send me a code')}
                                </button>
                            ) : (
                                <>
                                    {/* ההסבר על קוד שמגיע בשיחה - רק אחרי שהשיחה יצאה. */}
                                    {state.completionSent.method === 'VOICE' && <VoiceCallNotice />}

                                    <div className='ql-input'>
                                        <input
                                            type='tel'
                                            inputMode='numeric'
                                            autoComplete='one-time-code'
                                            id='completionCode'
                                            name='completionCode'
                                            dir='ltr'
                                            placeholder={t('Verification code')}
                                            value={state.completionCode}
                                            onChange={(e) => setState((s) => ({ ...s, completionCode: e.target.value, error: null }))}
                                        />
                                    </div>
                                    <button
                                        type='submit'
                                        className='ql-button'
                                        disabled={state.busy || submitRetry.isWaiting}
                                        aria-busy={state.busy ? 'true' : undefined}
                                    >
                                        {state.busy ? t('Checking...') : t('Login')}
                                    </button>
                                    <RetryNotice
                                        retry={submitRetry}
                                        i18nKey='Too many attempts. You can try again in {{time}}'
                                    />
                                    <button
                                        type='button'
                                        className='ql-link'
                                        disabled={sendRetry.isWaiting || state.busy}
                                        onClick={sendCompletionCode}
                                    >
                                        <Trans i18nKey="Didn't get the code? <strong>Send it again</strong>" />
                                    </button>
                                    <RetryNotice
                                        retry={sendRetry}
                                        i18nKey='A new code can be sent in {{time}}'
                                        tone='info'
                                    />
                                </>
                            )}

                            {state.completion.identityAllowed && (
                                <button
                                    type='button'
                                    className='ql-link'
                                    onClick={() => setState((s) => ({ ...s, useIdentity: true, error: null }))}
                                >
                                    {t('I do not have access to it - use my identity number')}
                                </button>
                            )}
                        </form>
                    )}

                    {(state.useIdentity || !state.completion?.secondChannel) && (
                        <form onSubmit={submitIdentity}>
                            <p className='ql-hint'>{t('Enter the identity number registered on the account')}</p>

                            <div className='ql-input'>
                                <input
                                    type='tel'
                                    inputMode='numeric'
                                    id='validId'
                                    name='validId'
                                    dir='ltr'
                                    placeholder={t('Identity number')}
                                    value={state.validId}
                                    onChange={(e) => setState((s) => ({ ...s, validId: e.target.value, error: null }))}
                                />
                            </div>

                            <div className='ql-options'>
                                {['ID', 'COM', 'PASSPORT'].map((type) => (
                                    <label key={type}>
                                        <input
                                            type='radio'
                                            name='validType'
                                            checked={state.validType === type}
                                            onChange={() => setState((s) => ({ ...s, validType: type }))}
                                        />
                                        <span>{t(type === 'ID' ? 'ID card' : type === 'COM' ? 'Company number' : 'Passport')}</span>
                                    </label>
                                ))}
                            </div>

                            <button
                                type='submit'
                                className='ql-button'
                                disabled={state.busy}
                                aria-busy={state.busy ? 'true' : undefined}
                            >
                                {state.busy ? t('Checking...') : t('Login')}
                            </button>

                            {state.completion?.secondChannel && (
                                <button
                                    type='button'
                                    className='ql-link'
                                    onClick={() => setState((s) => ({ ...s, useIdentity: false, error: null }))}
                                >
                                    {t('Send me a code instead')}
                                </button>
                            )}
                        </form>
                    )}
                </div>
            )}

            {/*
                הבורר הוא **חלון מלא** ולא רשימה בתוך הטופס - עם שמונה חשבונות
                העמודה הצרה הפכה את העמוד כולו לגלילה, והכותרת נעלמה למעלה.
                ראה `AccountChooser`. `channel` נמסר כדי שהערוץ שהוכח לא יוצג
                בכרטיסים: הוא זהה בכולם, וזו הסיבה שהם שם.
            */}
            {state.phase === PHASE_ACCOUNTS && (
                <AccountChooser
                    accounts={state.accounts}
                    channel={state.channel}
                    onChoose={chooseAccount}
                    onCancel={() => restart()}
                    busyRef={state.busyRef}
                    disabled={state.busy}
                />
            )}

            {state.notice && <p className='ql-notice'>{state.notice}</p>}
            {state.error && <p className='ql-error'><Trans i18nKey={state.error} /></p>}

            <div className='ql-footer'>
                {state.phase !== PHASE_IDENTIFIER && (
                    <button type='button' className='ql-link' onClick={() => restart()}>
                        {t('Start over')}
                    </button>
                )}
                {/*
                 * ההרשמה - **גם כאן, ולא רק במסך הסיסמה.**
                 *
                 * ההתחברות המהירה היא המסך שנפתח ראשון, כלומר היא המסך שלקוח חדש
                 * רואה. בלי הקישור הזה, מי שאין לו חשבון היה צריך לעבור קודם למסך
                 * הסיסמה - שאינו רלוונטי לו כלל - רק כדי למצוא את הדרך להירשם.
                 */}
                {showRegister && (
                    <button type='button' className='ql-link' onClick={() => navigate('/register')}>
                        <Trans i18nKey="Don't have an account yet? <strong>Register now</strong>" />
                    </button>
                )}
                {/* המעבר לצורה הישנה - מוצג בכל שלב, ולא בראשון בלבד: לקוח שנתקע
                    באמצע צריך דרך החוצה בלי לגלות אותה לבד. */}
                {onUsePassword && (
                    <button type='button' className='ql-link' onClick={onUsePassword}>
                        {t('Login with a username and password')}
                    </button>
                )}
            </div>
        </div>
    );
}
